import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, output, inject } from '@angular/core';
import { IQuizEditItem } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { AppTextService } from '@fi-app/shared/app-text/app-text.service';
import { FiOnboardingStepReopenedReason } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-enums';

@Component({
    selector: 'fi-quiz-start-info',
    templateUrl: './quiz-start-info.component.html',
    styleUrl: './quiz-start-info.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-quiz-start-info' },
    standalone: false,
})
export class FiQuizStartInfoComponent {
    public quiz = input.required<IQuizEditItem>();
    public isReopened = input<boolean>(false);
    public reopenedReason = input<FiOnboardingStepReopenedReason>();
    public startQuiz = output<void>();

    private readonly appTextService = inject(AppTextService);

    protected readonly quizInfo = computed(() => {
        const quiz = this.quiz();
        const isReopened = this.isReopened();

        if (quiz.AllowRetakes) {
            if (!isReopened) {
                return {
                    bannerTextId: 'onboarding.quiz_start_info_title_retakes_allowed',
                    bannerColorClass: 'blue',
                };
            }

            if (this.reopenedReason() === FiOnboardingStepReopenedReason.QuestionOrTodoItemAdded) {
                return {
                    bannerTextId: 'onboarding.quiz_reopened_info_title_retakes_allowed',
                    bannerColorClass: 'blue',
                };
            }
            if (this.reopenedReason() === FiOnboardingStepReopenedReason.StepElementSettingsChanged) {
                return {
                    bannerTextId: 'onboarding.quiz_failed_reopened_info_title_retakes_allowed',
                    bannerColorClass: 'blue',
                };
            }
            return {
                bannerTextId: 'onboarding.quiz_failed_reopened_info_title_retakes_allowed',
                bannerColorClass: 'blue',
            };
        }
        return {
            bannerTextId: isReopened
                ? 'onboarding.quiz_reopened_info_title_no_retakes'
                : 'onboarding.quiz_start_info_title_no_retakes',
            bannerColorClass: 'orange',
        };
    });

    protected quizTimerInfo = computed<{ bannerText: string; bannerColorClass: string } | undefined>(() => {
        const quiz = this.quiz();
        if (quiz.IsTimerEnabled) {
            const minutes = Math.floor(quiz.TimerMs / 60000);
            const remainingTimeMinutesLabel = `${minutes} ${this.appTextService.get('common.minutes').toLowerCase()}`;
            return {
                bannerText: this.appTextService.get('onboarding.start_quiz_timer_warning', {
                    '{0}': remainingTimeMinutesLabel,
                }),
                bannerColorClass: 'red',
            };
        }
        return undefined;
    });
}
